import _ from 'lodash'
import config from '../../../config'
import { calcIsUserLoggedIn } from '../../../App/utils'
import { log, logError, logErrorMessage } from '../../../utils/logger'

import * as deltaService from '../../../entities/outlook/deltas/service'

import { logoutThunk } from '../../../redux/thunks/auth'
import store from '../../../redux/store'

import { getDefaultSyncDateRangeISOs } from '../../../server/utils/date'
import queryClient from '../../../queryClient'
import { OUTLOOK_EVENTS_QUERY_KEY, OUTLOOK_EMAILS_QUERY_KEY } from '../../../hooks/queries/keys'

export default class SocketIOActions {
  constructor(socket, apolloClient, auth0, socketHandlers) {
    this.socket = socket
    this.apolloClient = apolloClient
    this.socketHandlers = socketHandlers
    this.auth0 = auth0
    this.logoutThunk = logoutThunk
  }

  safeSync = async syncFn => {
    try {
      const isUserLoggedIn = await calcIsUserLoggedIn(this.auth0)
      if (!isUserLoggedIn) return
      const result = await syncFn()
      return result
    } catch (e) {
      logError(e, 'safeSyncSocket')
    }
  }

  async eventReceived() {
    const haveEventsChanged = await this.safeSync(async () => {
      return this.syncOutlookEventDeltas()
    })
    if (this.socketHandlers.eventsUpdated !== null) {
      await this.socketHandlers.eventsUpdated(haveEventsChanged)
    }
  }

  async messageReceived() {
    const haveEmailsChanged = await this.safeSync(async () => {
      return this.syncOutlookMessageDeltas()
    })
    if (this.socketHandlers.emailsUpdated !== null) {
      await this.socketHandlers.emailsUpdated(haveEmailsChanged)
    }
  }

  async logout() {
    // Skipped during E2E: the web build points the socket at a remote server that rejects the
    // locally-signed E2E token, and its logout signal must not tear down the authenticated session.
    if (config.IS_TEST_E2E) return
    await store.dispatch(this.logoutThunk())
  }

  syncOutlookEventDeltas = async () => {
    try {
      const { startISO, endISO } = getDefaultSyncDateRangeISOs()

      const events = await deltaService.getEventDeltas({
        client: this.apolloClient,
        from: startISO,
        to: endISO,
      })

      const eventsToCreateCount = _.size(events.create)
      const eventsToModifyCount = _.size(events.modify)
      const eventsToDeleteCount = _.size(events.delete)

      const haveCalendarEventsChanged =
        eventsToCreateCount !== 0 || eventsToModifyCount !== 0 || eventsToDeleteCount !== 0

      log(
        `ES → LS → Event → C: ${eventsToCreateCount} U: ${eventsToModifyCount} D: ${eventsToDeleteCount}`,
      )

      if (!haveCalendarEventsChanged) {
        return haveCalendarEventsChanged
      }

      await queryClient.invalidateQueries({ queryKey: OUTLOOK_EVENTS_QUERY_KEY })

      return haveCalendarEventsChanged
    } catch (e) {
      logErrorMessage(
        e.message,
        'components/SocketIO/actions/actions.js > syncOutlookEventDeltas > e:',
      )
      return true
    }
  }

  syncOutlookMessageDeltas = async () => {
    try {
      const emails = await deltaService.getMessageDeltas({
        client: this.apolloClient,
        folderType: 0,
        selectType: 0,
      })

      const emailsToCreateCount = _.size(emails.create)
      const emailsToModifyCount = _.size(emails.modify)
      const emailsToDeleteCount = _.size(emails.delete)

      const haveEmailsChanged =
        emailsToCreateCount !== 0 || emailsToModifyCount !== 0 || emailsToDeleteCount !== 0

      log(
        `ES → LS → Email → C: ${emailsToCreateCount} U: ${emailsToModifyCount} D: ${emailsToDeleteCount}`,
      )

      if (!haveEmailsChanged) {
        return haveEmailsChanged
      }

      await queryClient.invalidateQueries({ queryKey: OUTLOOK_EMAILS_QUERY_KEY })

      return haveEmailsChanged
    } catch (e) {
      logErrorMessage(
        e.message,
        'components/SocketIO/actions/actions.js > syncOutlookMessageDeltas > e:',
      )
      return true
    }
  }
}
